import React, { useEffect, useMemo, useRef, useState } from "react";
import "./FilterTabs.css";

/**
 * Reusable sliding-underline tabs
 *
 * Props:
 * - tabs: Array<string | { key: string, label: string, badge?: number }>
 * - value: string (selected key)
 * - onChange: (key: string) => void
 * - className?: string
 * - size?: "sm" | "md"  (defaults to "md")
 * - layout?: "scroll" | "fixed" (defaults to "scroll")
 * - ariaLabel?: string  (for accessibility)
 */
export default function FilterTabs({
  tabs = [],
  value,
  onChange,
  className = "",
  size = "md",
  layout = "scroll", // ✅ NEW
  ariaLabel = "Filter options",
}) {
  // Normalize tabs to objects { key, label, badge? }
  const items = useMemo(
    () =>
      tabs.map((t) =>
        typeof t === "string" ? { key: t, label: t } : { ...t }
      ),
    [tabs]
  );

  const trackRef = useRef(null);
  const btnRefs = useRef({});
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });

  const updateSlider = () => {
    const track = trackRef.current;
    const activeBtn = btnRefs.current[value];
    if (!track || !activeBtn) return;

    const btnRect = activeBtn.getBoundingClientRect();
    const trackRect = track.getBoundingClientRect();

    // In scroll mode we compensate for scrollLeft; in fixed it’s always 0 (safe)
    const left = btnRect.left - trackRect.left + (track.scrollLeft || 0);
    const width = btnRect.width;

    setSliderStyle({ left, width });
  };

  // Keep slider under active tab; scroll it into view ONLY when layout="scroll"
  useEffect(() => {
    updateSlider();

    if (layout !== "scroll") return;

    const activeBtn = btnRefs.current[value];
    activeBtn?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [value, items.length, layout]);

  // Recalculate on resize AND while horizontally scrolling (scroll mode)
  useEffect(() => {
    const onResize = () => updateSlider();
    window.addEventListener("resize", onResize);

    const track = trackRef.current;
    let frame = 0;

    const onScroll = () => {
      if (layout !== "scroll") return;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        updateSlider();
        frame = 0;
      });
    };

    track?.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      track?.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [value, layout]);

  // Keyboard navigation
  const handleKeyDown = (e, idx) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const dir = e.key === "ArrowLeft" ? -1 : 1;
    const next = Math.min(Math.max(idx + dir, 0), items.length - 1);
    onChange(items[next].key);
    const nextBtn = btnRefs.current[items[next].key];
    nextBtn?.focus();
  };

  return (
    <div className={`filter-tabs ${className}`}>
      <div
        className={`filter-tabs__bar filter-tabs__bar--${size} filter-tabs__bar--${layout}`}
        role="tablist"
        aria-label={ariaLabel}
        ref={trackRef}
      >
        {items.map((item, idx) => {
          const active = item.key === value;
          return (
            <button
              key={item.key}
              role="tab"
              aria-selected={active}
              className={`filter-tabs__btn ${active ? "is-active" : ""}`}
              onClick={() => onChange(item.key)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              ref={(el) => (btnRefs.current[item.key] = el)}
              tabIndex={active ? 0 : -1}
              type="button"
            >
              <span className="filter-tabs__label">{item.label}</span>
              {typeof item.badge === "number" && item.badge > 0 && (
                <span className="filter-tabs__badge">
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </button>
          );
        })}

        {/* Animated underline */}
        <div
          className="filter-tabs__slider"
          style={{
            left: `${sliderStyle.left}px`,
            width: `${sliderStyle.width}px`,
          }}
        />
      </div>
    </div>
  );
}
